import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import {
  type ChildrenChartData,
  EARLY_AGE_CAP,
  HIGH_SCHOOL_AGE_CAP,
  PRIMARY_SCHOOL_AGE_CAP,
  mapFamilyGoalModelingPreviewToChartData,
} from "Charts/Utils/ChildrenChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { ArrowRightIcon } from "Components/_Shared/Icons/Icons";
import { type ChildrenGoalFormSchema, getChildrenGoalFormSchema } from "Forms/Schemas/Goals/childrenGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetFamilyGoalQueryKey,
  useClientDetail,
  useCreateFamilyGoal,
  useGetAnalysis,
  useGetFamilyGoalModeling,
  usePreviewFamilyGoalModeling,
  useUpdateFamilyGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  AnalysisDetailState,
  type FamilyGoalData,
  FamilyGoalDtoPriorityLevel,
  type FamilyGoalModelingPreview,
  type FamilyGoalResponseDto,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import {
  CHILDREN_INTEREST_RATE_LONG_TERM,
  CHILDREN_INTEREST_RATE_MID_TERM,
  CHILDREN_INTEREST_RATE_SHORT_TERM,
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { differenceInYears, format } from "date-fns";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChildLifePhase } from "./ChildLifePhase";
import { ChildrenGoalChart } from "./ChildrenGoalChart";
import { ModelingAccordion } from "./ModelingAccordion";

type Props = {
  readonly editGoal?: FamilyGoalResponseDto;
  readonly clientId: string | null;
  readonly onClose: () => void;
};

export const ChildrenGoalForm = forwardRef<{}, Props>(({ clientId, editGoal, onClose }, childrenGoalFormRef) => {
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const [chartData, setChartData] = useState<ChildrenChartData | null>(null);

  const { data: clientData } = useClientDetail(clientId ?? "", {
    query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });
  const birthDate = clientData?.birthDate;

  const methods = useForm<ChildrenGoalFormSchema>({
    resolver: yupResolver(getChildrenGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues:
      editGoal && editGoal.clientGuid === clientId
        ? {
            monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value,
            shortTermInterestRate: editGoal.modeling.shortTermAppreciationRate ?? undefined,
            midTermInterestRate: editGoal.modeling.midTermAppreciationRate ?? undefined,
            longTermInterestRate: editGoal.modeling.longTermAppreciationRate ?? undefined,
            includeInflation: editGoal.modeling.inflation?.enabled,
            inflationRate: editGoal.modeling.inflation?.rate ?? undefined,
            feeRate: editGoal.modeling.fee?.rate ?? undefined,
            includeFee: editGoal.modeling.fee?.enabled,
            includeEarlyAge: editGoal.data.earlyAge.active,
            earlyAgeAmount: editGoal.data.earlyAge.sectionAmount?.value,
            earlyAgeWithdrawalIn: editGoal.data.earlyAge.startWithdrawal?.value,
            includePrimarySchool: editGoal.data.primarySchool.active,
            primarySchoolAmount: editGoal.data.primarySchool.sectionAmount?.value,
            primarySchoolWithdrawalIn: editGoal.data.primarySchool.startWithdrawal?.value,
            includeHighSchool: editGoal.data.highSchool.active,
            highSchoolAmount: editGoal.data.highSchool.sectionAmount?.value,
            highSchoolWithdrawalIn: editGoal.data.highSchool.startWithdrawal?.value,
            includeUniversity: editGoal.data.university.active,
            universityAmount: editGoal.data.university.sectionAmount?.value,
            universityWithdrawalIn: editGoal.data.university.startWithdrawal?.value,
            includeStartToLife: editGoal.data.startToLife.active,
            startToLifeAmount: editGoal.data.startToLife.sectionAmount?.value,
            startToLifeWithdrawalIn: editGoal.data.startToLife.startWithdrawal?.value,
            referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
            note: editGoal.note || "",
            priority: editGoal.priorityLevel === FamilyGoalDtoPriorityLevel.high,
          }
        : {
            feeRate: DEFAULT_FEE_RATE,
            shortTermInterestRate: CHILDREN_INTEREST_RATE_SHORT_TERM,
            midTermInterestRate: CHILDREN_INTEREST_RATE_MID_TERM,
            longTermInterestRate: CHILDREN_INTEREST_RATE_LONG_TERM,
            inflationRate: DEFAULT_INFLATION_RATE,
            includeFee: true,
            includeInflation: true,
            includeEarlyAge: false,
            includePrimarySchool: false,
            includeHighSchool: false,
            includeUniversity: false,
            includeStartToLife: false,
            referenceStartDate: new Date(),
          },
  });

  const { control, setValue, reset, resetField, watch, handleSubmit } = methods;

  const inflationRate = watch("inflationRate");
  const shortTermInterestRate = watch("shortTermInterestRate");
  const midTermInterestRate = watch("midTermInterestRate");
  const longTermInterestRate = watch("longTermInterestRate");
  const feeRate = watch("feeRate");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");

  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");

  const includeEarlyAge = watch("includeEarlyAge");
  const includePrimarySchool = watch("includePrimarySchool");
  const includeHighSchool = watch("includeHighSchool");
  const includeUniversity = watch("includeUniversity");
  const includeStartToLife = watch("includeStartToLife");

  const earlyAgeAmount = watch("earlyAgeAmount");
  const earlyAgeWithdrawalIn = watch("earlyAgeWithdrawalIn");
  const primarySchoolAmount = watch("primarySchoolAmount");
  const primarySchoolWithdrawalIn = watch("primarySchoolWithdrawalIn");
  const highSchoolAmount = watch("highSchoolAmount");
  const highSchoolWithdrawalIn = watch("highSchoolWithdrawalIn");
  const universityAmount = watch("universityAmount");
  const universityWithdrawalIn = watch("universityWithdrawalIn");
  const startToLifeAmount = watch("startToLifeAmount");
  const startToLifeWithdrawalIn = watch("startToLifeWithdrawalIn");

  const referenceStartDate = watch("referenceStartDate");
  const clientAge = differenceInYears(referenceStartDate, birthDate ? new Date(birthDate) : referenceStartDate);

  const buildFamilyGoalSectionsData = useCallback(
    (): FamilyGoalData => ({
      earlyAge: {
        active: includeEarlyAge,
        sectionAmount: { currencyCode: "CZK", frequency: AmountFrequency.one_time, value: earlyAgeAmount || 0 },
        startWithdrawal: { type: TimeRangeType.year, value: earlyAgeWithdrawalIn || 0 },
      },
      primarySchool: {
        active: includePrimarySchool,
        sectionAmount: { currencyCode: "CZK", frequency: AmountFrequency.one_time, value: primarySchoolAmount || 0 },
        startWithdrawal: { type: TimeRangeType.year, value: primarySchoolWithdrawalIn || 0 },
      },
      highSchool: {
        active: includeHighSchool,
        sectionAmount: { currencyCode: "CZK", frequency: AmountFrequency.one_time, value: highSchoolAmount || 0 },
        startWithdrawal: { type: TimeRangeType.year, value: highSchoolWithdrawalIn || 0 },
      },
      university: {
        active: includeUniversity,
        sectionAmount: { currencyCode: "CZK", frequency: AmountFrequency.one_time, value: universityAmount || 0 },
        startWithdrawal: { type: TimeRangeType.year, value: universityWithdrawalIn || 0 },
      },
      startToLife: {
        active: includeStartToLife,
        sectionAmount: { currencyCode: "CZK", frequency: AmountFrequency.one_time, value: startToLifeAmount || 0 },
        startWithdrawal: { type: TimeRangeType.year, value: startToLifeWithdrawalIn || 0 },
      },
    }),
    [
      includeEarlyAge,
      earlyAgeAmount,
      earlyAgeWithdrawalIn,
      includePrimarySchool,
      primarySchoolAmount,
      primarySchoolWithdrawalIn,
      includeHighSchool,
      highSchoolAmount,
      highSchoolWithdrawalIn,
      includeUniversity,
      universityAmount,
      universityWithdrawalIn,
      includeStartToLife,
      startToLifeAmount,
      startToLifeWithdrawalIn,
    ],
  );

  const { mutate: previewFamilyGoalModeling } = usePreviewFamilyGoalModeling();

  const { data: isAnalysisInProgress } = useGetAnalysis(analysisId, versionId, {
    query: { select: (analysis) => analysis.state === AnalysisDetailState.in_progress },
  });

  // once the analysis leaves in_progress, the modeling must stay as it was while the analysis was being worked on,
  // so it's read from the frozen snapshot instead of being recomputed by the preview
  const groupId = editGoal?.groupGuid ?? "";
  const { data: frozenModeling } = useGetFamilyGoalModeling(analysisId, versionId, groupId, {
    query: { enabled: isAnalysisInProgress === false && !!groupId },
  });

  const applyModeling = useCallback(
    (modeling: FamilyGoalModelingPreview) => {
      const data = mapFamilyGoalModelingPreviewToChartData(modeling);
      setChartData(data);
      setValue("monthlyContributionWithFee", Math.round(data.totalMonthlyContribution));
    },
    [setValue],
  );

  const hasActiveSection = includeEarlyAge || includePrimarySchool || includeHighSchool || includeUniversity || includeStartToLife;

  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (!isAnalysisInProgress) {
      return;
    }

    if (!hasActiveSection) {
      previewRequestIdRef.current += 1;
      setChartData(null);
      setValue("monthlyContributionWithFee", 0);
      return;
    }

    const timeout = setTimeout(() => {
      const requestId = ++previewRequestIdRef.current;
      previewFamilyGoalModeling(
        {
          analysisId,
          versionId,
          data: {
            referenceStartDate: format(referenceStartDate, "yyyy-MM-dd"),
            data: buildFamilyGoalSectionsData(),
            modeling: {
              inflation: { enabled: !!includeInflation, rate: inflationRate },
              fee: { enabled: !!includeFee, rate: feeRate },
              shortTermAppreciationRate: shortTermInterestRate,
              midTermAppreciationRate: midTermInterestRate,
              longTermAppreciationRate: longTermInterestRate,
            },
          },
        },
        {
          onSuccess: (preview) => {
            if (requestId !== previewRequestIdRef.current) {
              return;
            }
            applyModeling(preview);
          },
        },
      );
    }, 400);

    return () => clearTimeout(timeout);
  }, [
    analysisId,
    versionId,
    isAnalysisInProgress,
    hasActiveSection,
    buildFamilyGoalSectionsData,
    shortTermInterestRate,
    midTermInterestRate,
    longTermInterestRate,
    inflationRate,
    includeInflation,
    feeRate,
    includeFee,
    referenceStartDate,
    previewFamilyGoalModeling,
    applyModeling,
    setValue,
  ]);

  useEffect(() => {
    if (clientId) {
      reset();
    }
  }, [clientId, reset]);

  // must stay below the reset effect, otherwise an already cached snapshot gets overwritten by the default values on mount
  useEffect(() => {
    if (frozenModeling) {
      applyModeling(frozenModeling);
    }
  }, [frozenModeling, applyModeling]);

  const { mutate: createFamilyGoal } = useCreateFamilyGoal();

  const { mutate: updateFamilyGoal } = useUpdateFamilyGoal();

  const onEditGoal = (data: ChildrenGoalFormSchema) => {
    updateFamilyGoal(
      {
        analysisId,
        versionId,
        groupId: editGoal?.groupGuid || "",
        data: {
          name: "",
          note: data.note || "",
          priorityLevel: data.priority ? FamilyGoalDtoPriorityLevel.high : FamilyGoalDtoPriorityLevel.low,
          clientGuid: clientId,
          data: buildFamilyGoalSectionsData(),
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            midTermAppreciationRate: data.midTermInterestRate,
            longTermAppreciationRate: data.longTermInterestRate,
            shortTermAppreciationRate: data.shortTermInterestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetFamilyGoalQueryKey(analysisId, versionId, editGoal?.groupGuid ?? undefined) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onCreateGoal = (data: ChildrenGoalFormSchema) => {
    createFamilyGoal(
      {
        analysisId,
        versionId,
        data: {
          name: "",
          note: data.note || "",
          priorityLevel: data.priority ? FamilyGoalDtoPriorityLevel.high : FamilyGoalDtoPriorityLevel.low,
          clientGuid: clientId,
          data: buildFamilyGoalSectionsData(),
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            midTermAppreciationRate: data.midTermInterestRate,
            longTermAppreciationRate: data.longTermInterestRate,
            shortTermAppreciationRate: data.shortTermInterestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  useImperativeHandle(childrenGoalFormRef, () => ({
    submitForm: () => {
      handleSubmit(editGoal ? onEditGoal : onCreateGoal)();
    },
  }));

  const shouldRenderModeling =
    monthlyContributionWithFee > 0 &&
    (includeEarlyAge || includePrimarySchool || includeHighSchool || includeUniversity || includeStartToLife);

  const shouldRenderChart =
    chartData &&
    ((includeEarlyAge && earlyAgeAmount && earlyAgeWithdrawalIn) ||
      (includePrimarySchool && primarySchoolAmount && primarySchoolWithdrawalIn) ||
      (includeHighSchool && highSchoolAmount && highSchoolWithdrawalIn) ||
      (includeUniversity && universityAmount && universityWithdrawalIn) ||
      (includeStartToLife && startToLifeAmount && startToLifeWithdrawalIn));

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.ChildLifePhases.Title)}
            </Typography>

            <Grid size={12} mb={Spacing.XL}>
              <FormCheckbox
                control={control}
                name="priority"
                label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                disabled={readOnly}
              />
            </Grid>

            <Grid container spacing={Spacing.XL}>
              {clientAge < EARLY_AGE_CAP && (
                <Grid size={12}>
                  <ChildLifePhase
                    name={t(Resources.AnalysisDetail.Goals.ChildLifePhases.EarlyAge)}
                    description={t(Resources.AnalysisDetail.Goals.ChildLifePhases.EarlyAgeDescription)}
                    renderSwitch={() => (
                      <Box mt={-2.5}>
                        <FormSwitch
                          control={control}
                          disabled={readOnly}
                          name="includeEarlyAge"
                          ariaLabel={t(Resources.AnalysisDetail.Goals.ChildLifePhases.EarlyAge)}
                          onChange={() => {
                            resetField("earlyAgeAmount");
                            resetField("earlyAgeWithdrawalIn");
                          }}
                        />
                      </Box>
                    )}
                  >
                    {includeEarlyAge ? (
                      <Grid container columnSpacing={Spacing.L}>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="earlyAgeAmount"
                            label={t(Resources.AnalysisDetail.Goals.Amount)}
                            required
                            scale={2}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.CZKSuffix)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="earlyAgeWithdrawalIn"
                            label={t(Resources.AnalysisDetail.Goals.WithdrawalIn)}
                            required
                            scale={2}
                            min={0}
                            max={100}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.Years)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    ) : undefined}
                  </ChildLifePhase>
                </Grid>
              )}
              {clientAge < PRIMARY_SCHOOL_AGE_CAP && (
                <Grid size={12}>
                  <ChildLifePhase
                    name={t(Resources.AnalysisDetail.Goals.ChildLifePhases.PrimarySchool)}
                    description={t(Resources.AnalysisDetail.Goals.ChildLifePhases.PrimarySchoolDescription)}
                    renderSwitch={() => (
                      <Box mt={-2.5}>
                        <FormSwitch
                          control={control}
                          disabled={readOnly}
                          name="includePrimarySchool"
                          ariaLabel={t(Resources.AnalysisDetail.Goals.ChildLifePhases.PrimarySchool)}
                          onChange={() => {
                            resetField("primarySchoolAmount");
                            resetField("primarySchoolWithdrawalIn");
                          }}
                        />
                      </Box>
                    )}
                  >
                    {includePrimarySchool ? (
                      <Grid container columnSpacing={Spacing.L}>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="primarySchoolAmount"
                            label={t(Resources.AnalysisDetail.Goals.Amount)}
                            required
                            scale={2}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.CZKSuffix)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="primarySchoolWithdrawalIn"
                            label={t(Resources.AnalysisDetail.Goals.WithdrawalIn)}
                            required
                            scale={2}
                            min={0}
                            max={100}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.Years)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    ) : undefined}
                  </ChildLifePhase>
                </Grid>
              )}

              {clientAge < HIGH_SCHOOL_AGE_CAP && (
                <Grid size={12}>
                  <ChildLifePhase
                    name={t(Resources.AnalysisDetail.Goals.ChildLifePhases.HighSchool)}
                    description={t(Resources.AnalysisDetail.Goals.ChildLifePhases.HighSchoolDescription)}
                    renderSwitch={() => (
                      <Box mt={-2.5}>
                        <FormSwitch
                          control={control}
                          disabled={readOnly}
                          name="includeHighSchool"
                          ariaLabel={t(Resources.AnalysisDetail.Goals.ChildLifePhases.HighSchool)}
                          onChange={() => {
                            resetField("highSchoolAmount");
                            resetField("highSchoolWithdrawalIn");
                          }}
                        />
                      </Box>
                    )}
                  >
                    {includeHighSchool ? (
                      <Grid container columnSpacing={Spacing.L}>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="highSchoolAmount"
                            label={t(Resources.AnalysisDetail.Goals.Amount)}
                            required
                            scale={2}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.CZKSuffix)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormNumberField
                            control={control}
                            name="highSchoolWithdrawalIn"
                            label={t(Resources.AnalysisDetail.Goals.WithdrawalIn)}
                            required
                            scale={2}
                            min={0}
                            max={100}
                            thousandsSeparator=" "
                            radix=","
                            mapToRadix={["."]}
                            unit={t(Resources.Common.Years)}
                            disabled={readOnly}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    ) : undefined}
                  </ChildLifePhase>
                </Grid>
              )}

              <Grid size={12}>
                <ChildLifePhase
                  name={t(Resources.AnalysisDetail.Goals.ChildLifePhases.University)}
                  description={t(Resources.AnalysisDetail.Goals.ChildLifePhases.UniversityDescription)}
                  renderSwitch={() => (
                    <Box mt={-2.5}>
                      <FormSwitch
                        control={control}
                        disabled={readOnly}
                        name="includeUniversity"
                        ariaLabel={t(Resources.AnalysisDetail.Goals.ChildLifePhases.University)}
                        onChange={() => {
                          resetField("universityAmount");
                          resetField("universityWithdrawalIn");
                        }}
                      />
                    </Box>
                  )}
                >
                  {includeUniversity ? (
                    <Grid container columnSpacing={Spacing.L}>
                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="universityAmount"
                          label={t(Resources.AnalysisDetail.Goals.Amount)}
                          required
                          scale={2}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="universityWithdrawalIn"
                          label={t(Resources.AnalysisDetail.Goals.WithdrawalIn)}
                          required
                          scale={2}
                          min={0}
                          max={100}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.Years)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  ) : undefined}
                </ChildLifePhase>
              </Grid>
              <Grid size={12}>
                <ChildLifePhase
                  name={t(Resources.AnalysisDetail.Goals.ChildLifePhases.StartToLife)}
                  description={t(Resources.AnalysisDetail.Goals.ChildLifePhases.StartToLifeDescription)}
                  renderSwitch={() => (
                    <Box mt={-2.4}>
                      <FormSwitch
                        control={control}
                        disabled={readOnly}
                        name="includeStartToLife"
                        ariaLabel={t(Resources.AnalysisDetail.Goals.ChildLifePhases.StartToLife)}
                        onChange={() => {
                          resetField("startToLifeAmount");
                          resetField("startToLifeWithdrawalIn");
                        }}
                      />
                    </Box>
                  )}
                >
                  {includeStartToLife ? (
                    <Grid container columnSpacing={Spacing.L}>
                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="startToLifeAmount"
                          label={t(Resources.AnalysisDetail.Goals.Amount)}
                          required
                          scale={2}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="startToLifeWithdrawalIn"
                          label={t(Resources.AnalysisDetail.Goals.WithdrawalIn)}
                          required
                          scale={2}
                          min={0}
                          max={100}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.Years)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  ) : undefined}
                </ChildLifePhase>
              </Grid>
            </Grid>
          </Box>
          <Box>
            {shouldRenderModeling && (
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      required
                      scale={2}
                      thousandsSeparator=" "
                      radix=","
                      mapToRadix={["."]}
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="shortTermInterestRate"
                      label={t(Resources.AnalysisDetail.Goals.ShortTermInterestRate)}
                      required
                      scale={1}
                      min={0}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (Number(newInterestRate) === Number(inflationRate)) {
                          setValue("shortTermInterestRate", shortTermInterestRate);
                          return;
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="midTermInterestRate"
                      label={t(Resources.AnalysisDetail.Goals.MidTermInterestRate)}
                      required
                      scale={1}
                      min={0}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (Number(newInterestRate) === Number(inflationRate)) {
                          setValue("midTermInterestRate", midTermInterestRate);
                          return;
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="longTermInterestRate"
                      label={t(Resources.AnalysisDetail.Goals.LongTermInterestRate)}
                      required
                      scale={1}
                      min={0}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (Number(newInterestRate) === Number(inflationRate)) {
                          setValue("longTermInterestRate", longTermInterestRate);
                          return;
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;
                        if (
                          [Number(shortTermInterestRate), Number(midTermInterestRate), Number(longTermInterestRate)].includes(
                            newInflationRate,
                          )
                        ) {
                          return;
                        }

                        setValue("inflationRate", newInflationRate);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onChange={(newInflationRate) => {
                        if (
                          [Number(shortTermInterestRate), Number(midTermInterestRate), Number(longTermInterestRate)].includes(
                            Number(newInflationRate),
                          )
                        ) {
                          setValue("inflationRate", inflationRate);
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;
                        if (newFeeRate === Number(feeRate)) {
                          return;
                        }

                        setValue("feeRate", newFeeRate);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      scale={1}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeFee || readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            )}
          </Box>
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && chartData && (
                <>
                  <Grid size={12}>
                    <Flex mx={Spacing.XXXL} justifyContent="space-between" alignItems="center">
                      <Flex alignItems="center" justifyContent="center" flexDirection="column">
                        <Typography display="block" variant="caption" fontWeight={600}>
                          {formatCurrency(chartData.totalAmountRequired, CurrencyCode.CZK, 0)}
                        </Typography>
                        <Typography variant="caption">{t(Resources.AnalysisDetail.Goals.TotalRequiredAmount)}</Typography>
                      </Flex>
                      <ArrowRightIcon color="primary" />
                      <Flex alignItems="center" justifyContent="center" flexDirection="column">
                        <Typography display="block" variant="caption" fontWeight={600}>
                          {formatCurrency(chartData.totalAmountInvested, CurrencyCode.CZK, 0)}
                        </Typography>
                        <Typography variant="caption">{t(Resources.AnalysisDetail.Goals.TotalInvestedAmount)}</Typography>
                      </Flex>
                      <ArrowRightIcon color="primary" />
                      <Flex alignItems="center" justifyContent="center" flexDirection="column">
                        <Typography color="success" display="block" variant="caption" fontWeight={600}>
                          {formatCurrency(chartData.totalAmountRequired - chartData.totalAmountInvested, CurrencyCode.CZK, 0)}
                        </Typography>
                        <Typography variant="caption">{t(Resources.AnalysisDetail.Goals.InterestRate)}</Typography>
                      </Flex>
                    </Flex>
                  </Grid>
                  <Grid size={12}>
                    <ChildrenGoalChart
                      data={chartData}
                      earlyAgeWithdrawalIn={earlyAgeWithdrawalIn}
                      primarySchoolWithdrawalIn={primarySchoolWithdrawalIn}
                      highSchoolWithdrawalIn={highSchoolWithdrawalIn}
                      universityWithdrawalIn={universityWithdrawalIn}
                      startToLifeWithdrawalIn={startToLifeWithdrawalIn}
                      referenceStartDate={referenceStartDate}
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
